import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/context/LanguageContext';
import { inquiriesAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles, ArrowRight, ArrowLeft,
  Upload, CheckCircle, Send, Crown, Palette, Frame
} from 'lucide-react';

const styles = [
  { id: 'classic', icon: Crown, label: 'Classic', desc: 'Elegant, timeless engraving with serif typography' },
  { id: 'comic', icon: Palette, label: 'Comic', desc: 'Bold, playful illustrations with modern flair' },
  { id: 'portrait', icon: Frame, label: 'Portrait', desc: 'Photo-realistic laser etching from your image' },
];

const materials = ['Slate', 'Wood', 'Metal', 'Leather', 'Glass', 'Acrylic'];

export default function InquiryPage() {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    style: '',
    name: '',
    email: '',
    phone: '',
    material: '',
    description: '',
    file: null,
    fileUrl: null,
    marketing: false,
  });
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const stepLabels = [t('inquiry.step1'), t('inquiry.step2'), t('inquiry.step3'), t('inquiry.step4')];
  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const canProceed = () => {
    if (step === 0) return !!form.style;
    if (step === 1) return form.name && form.email && form.material;
    return true;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAPI.upload(file);
      updateForm('file', { name: file.name, size: `${(file.size / 1024 / 1024).toFixed(1)} MB` });
      updateForm('fileUrl', res.data.file_url);
      toast.success('File uploaded successfully!');
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await inquiriesAPI.create({
        customer_name: form.name,
        contact: form.email,
        material_type: form.material,
        style: form.style,
        file_url: form.fileUrl,
        description: form.description,
        marketing_consent: form.marketing,
      });
      toast.success('Inquiry submitted successfully! We will contact you shortly.');
      setStep(0);
      setForm({ style: '', name: '', email: '', phone: '', material: '', description: '', file: null, fileUrl: null, marketing: false });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-20">
      <section ref={ref} className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <Badge variant="outline" className="mb-4 border-accent/30 text-accent bg-accent/5 text-xs tracking-wider uppercase">
            <Sparkles className="w-3 h-3 mr-2" />
            {t('inquiry.title')}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-title mb-4">
            {t('inquiry.title')}
          </h1>
          <p className="text-base text-body">
            {t('inquiry.subtitle')}
          </p>
        </motion.div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-10">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                  i <= step
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-body'
                }`}
              >
                {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block ${
                i <= step ? 'text-primary' : 'text-body'
              }`}>
                {label}
              </span>
              {i < stepLabels.length - 1 && (
                <div className={`flex-1 h-[1px] ${i < step ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-6 sm:p-8">
            {step === 0 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
                <h3 className="text-lg font-semibold text-title mb-6">{t('inquiry.selectStyle')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {styles.map((s) => {
                    const Icon = s.icon;
                    const isSelected = form.style === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => updateForm('style', s.id)}
                        className={`group p-5 rounded-xl border text-left ${
                          isSelected ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-border/80'
                        }`}
                        style={{ transition: 'border-color 0.3s ease, background 0.3s ease' }}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                          isSelected ? 'bg-primary/15 text-primary' : 'bg-secondary text-body'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <p className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-title'}`}>{s.label}</p>
                        <p className="text-xs text-body mt-1">{s.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm text-title mb-2 block">{t('inquiry.name')}</Label>
                    <Input value={form.name} onChange={(e) => updateForm('name', e.target.value)} className="bg-secondary border-border text-title" placeholder="John Doe" />
                  </div>
                  <div>
                    <Label className="text-sm text-title mb-2 block">{t('inquiry.email')}</Label>
                    <Input type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} className="bg-secondary border-border text-title" placeholder="john@example.com" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm text-title mb-2 block">{t('inquiry.phone')}</Label>
                    <Input value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} className="bg-secondary border-border text-title" placeholder="+972 54 123 4567" />
                  </div>
                  <div>
                    <Label className="text-sm text-title mb-2 block">{t('inquiry.material')}</Label>
                    <Select value={form.material} onValueChange={(v) => updateForm('material', v)}>
                      <SelectTrigger className="bg-secondary border-border text-title"><SelectValue placeholder="Select material" /></SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {materials.map((m) => (<SelectItem key={m} value={m.toLowerCase()} className="text-title">{m}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-title mb-2 block">{t('inquiry.description')}</Label>
                  <Textarea value={form.description} onChange={(e) => updateForm('description', e.target.value)} className="bg-secondary border-border text-title min-h-[100px]" placeholder="Describe your vision..." />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
                <h3 className="text-lg font-semibold text-title mb-4">{t('inquiry.uploadFile')}</h3>
                <p className="text-sm text-body mb-6">Upload a reference image, logo, or design file (optional).</p>
                <label
                  className="border-2 border-dashed border-border rounded-xl p-12 text-center hover:border-primary/30 cursor-pointer block"
                  style={{ transition: 'border-color 0.3s ease' }}
                >
                  <input type="file" className="hidden" accept="image/*,.pdf,.ai" onChange={handleFileUpload} />
                  <Upload className="w-8 h-8 text-body mx-auto mb-3" />
                  <p className="text-sm text-body">Click to upload or drag and drop</p>
                  <p className="text-xs text-body mt-1">PNG, JPG, PDF, AI up to 10MB</p>
                </label>
                {form.file && (
                  <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-sm text-title">{form.file.name}</span>
                    <span className="text-xs text-body">{form.file.size}</span>
                  </div>
                )}
                <div className="mt-8 flex items-start gap-3">
                  <Checkbox
                    checked={form.marketing}
                    onCheckedChange={(v) => updateForm('marketing', v)}
                    className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <p className="text-xs text-body">{t('inquiry.marketingOptIn')}</p>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
                <h3 className="text-lg font-semibold text-title mb-6">Review Your Inquiry</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Style', value: form.style || 'Not selected' },
                    { label: 'Name', value: form.name || '\u2014' },
                    { label: 'Email', value: form.email || '\u2014' },
                    { label: 'Phone', value: form.phone || '\u2014' },
                    { label: 'Material', value: form.material || '\u2014' },
                    { label: 'Description', value: form.description || '\u2014' },
                    { label: 'File', value: form.file?.name || 'None' },
                    { label: 'Marketing Consent', value: form.marketing ? 'Yes' : 'No' },
                  ].map((item) => (
                    <div key={item.label} className="flex justify-between items-start py-2 border-b border-border last:border-0">
                      <span className="text-sm text-body">{item.label}</span>
                      <span className="text-sm text-title text-right max-w-[60%] capitalize">{item.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 0}
                className="gap-2 border-border text-title hover:bg-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('inquiry.back')}
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep((s) => s + 1)} disabled={!canProceed()} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-cyan">
                  {t('inquiry.next')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-cyan">
                  <Send className="w-4 h-4" />
                  {submitting ? 'Submitting...' : t('inquiry.submit')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
